import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from 'react';
import {
  ModalContent,
  ConfigTableContainer,
  CheckBoxContainer,
  TextfieldWrapper,
  InputWrapper
} from './styles';
import {
  ECodeType,
  ESpreadsheetField,
  IOrderEntryPreference
} from '../../../../../../store/ducks/user/types';
import { Chips, Table } from '@gerdau/hefesto-mais-web';
import { Checkbox, Modal, TextField } from '@gerdau/hefesto';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';

interface SpreadsheetConfigModalProps {
  canShowModal: boolean;
  orderEntryPreference: IOrderEntryPreference;
  onCancelClick(): void;
  onConfirmClick(
    orderEntryPreference: IOrderEntryPreference,
    hasConfigChanged: boolean
  ): void;
  setCanShowModal: Dispatch<SetStateAction<boolean>>;
}

const SpreadsheetConfigModal: React.FC<SpreadsheetConfigModalProps> = ({
  canShowModal,
  setCanShowModal,
  onCancelClick,
  onConfirmClick,
  orderEntryPreference
}) => {
  const getColumnPosition = (field: ESpreadsheetField): string => {
    const item = orderEntryPreference.spreadsheetColumns.find(
      (val) => val.field === field
    );
    return item ? item.position : '';
  };

  const [codeType, setCodeType] = useState<ECodeType>(
    orderEntryPreference.codeType
  );

  const [spreadsheetColumns, setSpreadsheetColumns] = useState<
    ESpreadsheetField[]
  >(
    orderEntryPreference.spreadsheetColumns
      .filter((col) => col.position !== '')
      .map((col) => col.field)
  );

  const [skuColumn, setSkuColumn] = useState(
    getColumnPosition(ESpreadsheetField.SKU)
  );
  const [clientSkuColumn, setClientSkuColumn] = useState(
    getColumnPosition(ESpreadsheetField.CLIENT_SKU)
  );
  const [quantityColumn, setQuantityColumn] = useState(
    getColumnPosition(ESpreadsheetField.QUANTITY)
  );
  const [unityColumn, setUnityColumn] = useState(
    getColumnPosition(ESpreadsheetField.UNITY)
  );
  const [dateColumn, setDateColumn] = useState(
    getColumnPosition(ESpreadsheetField.DESIRED_DATE)
  );
  const [customerPurchaseItemColumn, setCustomerPurchaseItemColumn] = useState(
    getColumnPosition(ESpreadsheetField.CUSTOMER_PURCHASE_ITEM)
  );

  const [startValuesRow, setStartValuesRow] = useState(
    orderEntryPreference.spreadsheetLine
  );

  const dispatch = useDispatch();

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    updateStateFunction: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const onlyLettersRegex = new RegExp(/^[A-Za-z]+$/);
    const typedText = e.target.value as string;
    if (onlyLettersRegex.test(typedText) || typedText === '') {
      updateStateFunction(typedText.toUpperCase());
    } else {
      e.preventDefault();
    }
  };

  const hasErrorInAnyAdditionalInfo = (): boolean => {
    return (
      (codeType === ECodeType.SKU && skuColumn === '') ||
      (codeType === ECodeType.CLIENT_SKU && clientSkuColumn === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.QUANTITY) &&
        quantityColumn === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.UNITY) &&
        unityColumn === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.DESIRED_DATE) &&
        dateColumn === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.CUSTOMER_PURCHASE_ITEM) &&
        customerPurchaseItemColumn === '')
    );
  };

  const onStartValuesLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbersRegex = new RegExp(/^[0-9]{1,3}$/);
    const typedNumber = e.target.value;
    if (onlyNumbersRegex.test(typedNumber) || typedNumber === '') {
      setStartValuesRow(Number(typedNumber));
    } else {
      e.preventDefault();
    }
  };

  const handleSelectChips = (selectedChip: string[]) => {
    selectedChip[0] === ECodeType.SKU && setCodeType(ECodeType.SKU);
    selectedChip[0] === ECodeType.CLIENT_SKU &&
      setCodeType(ECodeType.CLIENT_SKU);
  };

  const hasErrorInStartValuesLine = (): boolean => {
    return !startValuesRow || startValuesRow < 1;
  };

  const onConfirmConfig = () => {
    let hasConfigChanged = false;
    orderEntryPreference.spreadsheetColumns.forEach((column) => {
      switch (column.field) {
        case ESpreadsheetField.SKU:
          if (skuColumn !== column.position) {
            column.position = skuColumn;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.CLIENT_SKU:
          if (clientSkuColumn !== column.position) {
            column.position = clientSkuColumn;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.QUANTITY:
          if (quantityColumn !== column.position) {
            column.position = quantityColumn;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.UNITY:
          if (unityColumn !== column.position) {
            column.position = unityColumn;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.DESIRED_DATE:
          if (dateColumn !== column.position) {
            column.position = dateColumn;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.CUSTOMER_PURCHASE_ITEM:
          if (customerPurchaseItemColumn !== column.position) {
            column.position = customerPurchaseItemColumn;
            hasConfigChanged = true;
          }
          break;
        default:
          break;
      }
    });

    if (
      codeType !== orderEntryPreference.codeType ||
      startValuesRow !== orderEntryPreference.spreadsheetLine
    ) {
      orderEntryPreference.codeType = codeType;
      orderEntryPreference.spreadsheetLine = startValuesRow;
      hasConfigChanged = true;
    }
    onConfirmClick(orderEntryPreference, hasConfigChanged);
  };

  interface ConfigTableDataSource {
    key: number;
    skuColumn: string;
    clientSkuColumn: string;
    quantityColumn: string;
    unityColumn: string;
    dateColumn: string;
    customerPurchaseItemColumn: string;
  }
  const dataSource: ConfigTableDataSource[] = [
    {
      key: 1,
      skuColumn: skuColumn,
      clientSkuColumn: clientSkuColumn,
      quantityColumn: quantityColumn,
      unityColumn: unityColumn,
      dateColumn: dateColumn,
      customerPurchaseItemColumn: customerPurchaseItemColumn
    }
  ];

  const getColumns = () => {
    const columns = [
      {
        title: 'Código Gerdau',
        dataIndex: 'skuColumn',
        render: () => (
          <InputWrapper>
            <TextField
              label={''}
              maxLength={2}
              name={'Código Gerdau'}
              value={skuColumn}
              onChange={(e) => onInputChange(e, setSkuColumn)}
            />
          </InputWrapper>
        )
      },
      {
        title: 'Código interno',
        dataIndex: 'clientSkuColumn',
        render: () => (
          <InputWrapper>
            <TextField
              label={''}
              maxLength={2}
              name={'Código interno'}
              value={clientSkuColumn}
              onChange={(e) => onInputChange(e, setClientSkuColumn)}
            />
          </InputWrapper>
        )
      },
      {
        title: 'Quantidade',
        dataIndex: 'quantityColumn',
        render: () => (
          <InputWrapper>
            <TextField
              label={''}
              maxLength={2}
              name={'Quantidade'}
              value={quantityColumn}
              onChange={(e) => onInputChange(e, setQuantityColumn)}
            />
          </InputWrapper>
        )
      },
      {
        title: 'Item do pedido',
        dataIndex: 'customerPurchaseItemColumn',
        render: () => (
          <InputWrapper>
            <TextField
              label={''}
              maxLength={2}
              name={'Item do pedido'}
              value={customerPurchaseItemColumn}
              onChange={(e) => onInputChange(e, setCustomerPurchaseItemColumn)}
            />
          </InputWrapper>
        )
      },
      {
        title: 'Unidade de medida',
        dataIndex: 'unityColumn',
        render: () => (
          <InputWrapper>
            <TextField
              size={3}
              label={''}
              maxLength={2}
              name={'Unidade de medida'}
              value={unityColumn}
              onChange={(e) => onInputChange(e, setUnityColumn)}
            />
          </InputWrapper>
        )
      },
      {
        title: 'Data desejada',
        dataIndex: 'dateColumn',
        render: () => (
          <InputWrapper>
            <TextField
              label={''}
              maxLength={2}
              name={'Data desejada'}
              value={dateColumn}
              onChange={(e) => onInputChange(e, setDateColumn)}
            />
          </InputWrapper>
        )
      }
    ];

    const hideColumns: string[] = [];

    if (codeType === ECodeType.CLIENT_SKU) {
      hideColumns.push('skuColumn'); // Material Gerdau
    } else {
      hideColumns.push('clientSkuColumn'); // Material cliente
    }

    !spreadsheetColumns.includes(ESpreadsheetField.QUANTITY) &&
      hideColumns.push('quantityColumn');
    !spreadsheetColumns.includes(ESpreadsheetField.UNITY) &&
      hideColumns.push('unityColumn');
    !spreadsheetColumns.includes(ESpreadsheetField.DESIRED_DATE) &&
      hideColumns.push('dateColumn');
    !spreadsheetColumns.includes(ESpreadsheetField.CUSTOMER_PURCHASE_ITEM) &&
      hideColumns.push('customerPurchaseItemColumn');

    return columns.filter((col) => !hideColumns.includes(col.dataIndex));
  };

  const handleCheckBox = (
    e: ChangeEvent<HTMLInputElement>,
    itemColumn: ESpreadsheetField,
    // eslint-disable-next-line @typescript-eslint/ban-types
    updateStateFunction: Function
  ): void => {
    if (!e.target.checked) {
      updateStateFunction('');
      setSpreadsheetColumns(
        spreadsheetColumns.filter((item) => item !== e.target.name)
      );
    } else {
      const columns = [...spreadsheetColumns, itemColumn];
      setSpreadsheetColumns(columns);
    }
  };

  const getFilterInfo = (): string => {
    const selectedCheckboxs: string[] = [];
    spreadsheetColumns.forEach((col) => {
      col === ESpreadsheetField.QUANTITY && selectedCheckboxs.push('qtd');
      col === ESpreadsheetField.CUSTOMER_PURCHASE_ITEM &&
        selectedCheckboxs.push('itmPdd');
      col === ESpreadsheetField.DESIRED_DATE && selectedCheckboxs.push('data');
      col === ESpreadsheetField.UNITY && selectedCheckboxs.push('uni');
    });
    return `#tpCod:${
      (codeType === ECodeType.SKU && 'cod_gerdau') ||
      (codeType === ECodeType.CLIENT_SKU && 'cod_interno') ||
      'not_set'
    }
    #cln:[${selectedCheckboxs.join(';')}]`;
  };

  const prepareAndSendTagging = (itemName: string, itemDescription: string) => {
    dispatch(
      sendTagging({
        screenName:
          'LG:Comprar:AcosLongos:PedidoViaPlanilha:Selecao:ModalConfiguracao',
        itemName: itemName,
        itemDescription: itemDescription,
        event: 'click',
        filterInfo: getFilterInfo()
      })
    );
  };

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:Comprar:AcosLongos:PedidoViaPlanilha:ModalConfiguracao',
        event: 'page_view',
        page: document.location.pathname
      })
    );
  }, [dispatch, spreadsheetColumns]);

  return (
    <>
      {canShowModal && (
        <Modal
          modalSize="md"
          buttons={{
            left: {
              children: 'Cancelar',
              name: 'Cancelar',
              onClick: () => {
                prepareAndSendTagging(
                  'button:cancelar',
                  'comprar_ViaPlanilha_modalConfiguracao_cancelar'
                );
                onCancelClick();
                setCanShowModal(false);
              },
              type: 'button'
            },
            right: {
              children: 'Avançar',
              name: 'Avançar',
              onClick: () => {
                prepareAndSendTagging(
                  'button:avancar',
                  'comprar_ViaPlanilha_modalConfiguracao_avancar'
                );
                onConfirmConfig();
                setCanShowModal(false);
              },
              type: 'button',
              disabled:
                hasErrorInAnyAdditionalInfo() || hasErrorInStartValuesLine()
            }
          }}
          onClose={() => setCanShowModal(false)}
          title="Configurar planilha"
        >
          <ModalContent>
            <div>
              <span>Qual código de material você utiliza?</span>
              <Chips
                type={'choice'}
                selectedChipIds={[codeType]}
                handleSelect={(id) => handleSelectChips(id)}
                chips={[
                  {
                    id: ECodeType.SKU,
                    label: 'Código Gerdau de material'
                  },
                  {
                    id: ECodeType.CLIENT_SKU,
                    label: 'Meu código interno de material'
                  }
                ]}
              />
            </div>

            <div>
              <span>
                Marque as informações adicionais que se encontram na sua
                planilha:
              </span>
              <CheckBoxContainer>
                <Checkbox
                  label={'Quantidade'}
                  checked={spreadsheetColumns.includes(
                    ESpreadsheetField.QUANTITY
                  )}
                  name={ESpreadsheetField.QUANTITY}
                  key={'Quantidade'}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                    handleCheckBox(
                      e,
                      ESpreadsheetField.QUANTITY,
                      setQuantityColumn
                    )
                  }
                />
                <Checkbox
                  label={'Item do pedido'}
                  checked={spreadsheetColumns.includes(
                    ESpreadsheetField.CUSTOMER_PURCHASE_ITEM
                  )}
                  name={ESpreadsheetField.CUSTOMER_PURCHASE_ITEM}
                  key={'Item do pedido'}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                    handleCheckBox(
                      e,
                      ESpreadsheetField.CUSTOMER_PURCHASE_ITEM,
                      setCustomerPurchaseItemColumn
                    )
                  }
                />
                <Checkbox
                  label={'Unidade de medida'}
                  checked={spreadsheetColumns.includes(ESpreadsheetField.UNITY)}
                  name={ESpreadsheetField.UNITY}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                    handleCheckBox(e, ESpreadsheetField.UNITY, setUnityColumn)
                  }
                />
                <Checkbox
                  label={'Data desejada'}
                  checked={spreadsheetColumns.includes(
                    ESpreadsheetField.DESIRED_DATE
                  )}
                  name={ESpreadsheetField.DESIRED_DATE}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                    handleCheckBox(
                      e,
                      ESpreadsheetField.DESIRED_DATE,
                      setDateColumn
                    )
                  }
                />
              </CheckBoxContainer>
            </div>

            <ConfigTableContainer>
              <p>Organize as colunas de acordo com a sua planilha</p>
              <Table columns={getColumns()} dataSource={dataSource} />
            </ConfigTableContainer>

            <div>
              <span>
                Em qual linha da planilha começam as informações dos itens do
                seu pedido?
              </span>
              <TextfieldWrapper>
                <TextField
                  variant={hasErrorInStartValuesLine() ? 'danger' : undefined}
                  value={startValuesRow}
                  onChange={(e) => onStartValuesLineChange(e)}
                  label={'Número da linha'}
                  name={'Número da linha'}
                  placeholder={'Número da linha'}
                />
              </TextfieldWrapper>
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default SpreadsheetConfigModal;
